/**
 * Export das divergências da conferência (passo 7) — 01/09/2026, pedido do
 * dono: gerar um pacote para enviar de volta ao agente de IA para análise e
 * correção. Dois formatos, MESMO conteúdo e MESMO agrupamento da tela
 * (classe × natureza — divergencias-grupos.ts):
 *
 * - `.md`  — legível: cabeçalho (competência, data/hora, ids), resumo por
 *   severidade/status e os grupos/sub-grupos com cada ocorrência;
 * - `.json` — machine-readable (`schemaVersao: "divergencias-export@v1"` para o
 *   futuro import reconhecer): cada divergência com todos os campos do modelo,
 *   a chave/rótulo de natureza e — quando unidade = "centavos" — os valores
 *   também em REAIS decimais (calculadoReais/esperadoReais/deltaReais), para o
 *   agente não precisar converter.
 *
 * O export é SEMPRE sem os filtros da tela: a página busca TODAS as
 * divergências da competência (GET sem query) antes de gerar. Módulo puro —
 * a renderização/download ficam na página.
 */
import { agruparDivergencias, naturezaDaDivergencia, type DivergenciaItem, type GrupoDivergencias } from "./divergencias-grupos";
import { fmtMoeda, fmtHhMm } from "./format";

export const SCHEMA_VERSAO_DIVERGENCIAS_EXPORT = "divergencias-export@v1";

/** Contexto da exportação (cabeçalho nos dois formatos). */
export interface ContextoExportDivergencias {
  /** Id numérico da competência (ex.: 202608). */
  competenciaId: number;
  /** Rótulo "MM/AAAA" (ex.: "08/2026"). */
  competenciaRotulo: string;
  /** ISO da geração (ex.: new Date().toISOString()). */
  geradoEmIso: string;
  /** Último retorno Contmega importado, quando houver. */
  retornoId?: string | null;
  /** Run mensal de referência da conferência, quando houver. */
  runId?: string | null;
  /** Origem da exportação (02/09/2026, pedido do dono — saber de ONDE o arquivo
   *  saiu: produção × local): window.location.origin (ex.: "http://76.13.169.70:3000"). */
  origemUrl?: string | null;
}

/** Alvo decomposto (o alvo é "escopo|pessoa|eventoOuCampo|campo"). */
function decomporAlvo(alvo: string) {
  const [escopo, pessoa, eventoOuCampo, campo] = alvo.split("|");
  return {
    escopo: escopo ?? "",
    pessoa: pessoa || null,
    eventoOuCampo: eventoOuCampo || null,
    campo: campo || null,
  };
}

const centavosParaReais = (v: number) => Math.round(v) / 100;

/** Item do JSON: todos os campos do modelo + natureza + valores em R$. */
export function itemExportJson(d: DivergenciaItem, natureza: { chave: string; rotulo: string }) {
  const emCentavos = d.unidade === "centavos";
  return {
    id: d.id,
    nivel: d.nivel,
    classe: d.classe,
    severidade: d.severidade,
    status: d.status,
    alvo: d.alvo,
    alvoPartes: decomporAlvo(d.alvo),
    descricao: d.descricao,
    calculado: d.calculado ?? null,
    esperado: d.esperado ?? null,
    delta: d.delta ?? null,
    unidade: d.unidade ?? null,
    // valores em R$ decimais quando a unidade é centavos (facilita o agente)
    ...(emCentavos
      ? {
          calculadoReais: d.calculado !== null && d.calculado !== undefined ? centavosParaReais(d.calculado) : null,
          esperadoReais: d.esperado !== null && d.esperado !== undefined ? centavosParaReais(d.esperado) : null,
          deltaReais: d.delta !== null && d.delta !== undefined ? centavosParaReais(d.delta) : null,
        }
      : {}),
    justificativa: d.justificativa ?? null,
    referenciaEstudo: d.referenciaEstudo ?? null,
    grupo: { classe: d.classe, naturezaChave: natureza.chave, naturezaRotulo: natureza.rotulo },
  };
}

function resumoDaLista(lista: DivergenciaItem[]) {
  const contar = (campo: "severidade" | "status") => {
    const m: Record<string, number> = {};
    for (const d of lista) m[d[campo]] = (m[d[campo]] ?? 0) + 1;
    return m;
  };
  return {
    total: lista.length,
    porSeveridade: contar("severidade"),
    porStatus: contar("status"),
    bloqueantesAbertas: lista.filter((d) => d.severidade === "bloqueante" && d.status === "aberta").length,
  };
}

/** Documento JSON completo do export (schemaVersao + cabeçalho + grupos + lista plana). */
export function montarExportJson(divergencias: DivergenciaItem[], ctx: ContextoExportDivergencias) {
  const grupos = agruparDivergencias(divergencias);
  return {
    schemaVersao: SCHEMA_VERSAO_DIVERGENCIAS_EXPORT,
    competencia: ctx.competenciaId,
    competenciaRotulo: ctx.competenciaRotulo,
    geradoEm: ctx.geradoEmIso,
    retornoId: ctx.retornoId ?? null,
    runId: ctx.runId ?? null,
    origem: ctx.origemUrl ?? null,
    resumo: { ...resumoDaLista(divergencias), grupos: grupos.length },
    grupos: grupos.map((g) => ({
      classe: g.classe,
      total: g.itens.length,
      abertas: g.abertas,
      bloqueantesAbertas: g.bloqueantesAbertas,
      piorSeveridade: g.piorSeveridade,
      subGrupos: g.subGrupos.map((sg) => ({
        naturezaChave: sg.chave,
        naturezaRotulo: sg.rotulo,
        total: sg.itens.length,
        abertas: sg.abertas,
        ids: sg.itens.map((d) => d.id),
      })),
    })),
    divergencias: divergencias.map((d) => itemExportJson(d, naturezaDaDivergencia(d))),
  };
}

/** Valor legível no MD (mesma regra da tela): centavos → R$, minutos → h:mm, p.p. → %. */
function fmtValorExport(v: number | null | undefined, unidade: string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (unidade === "minutos") return fmtHhMm(v);
  if (unidade === "p.p.") return `${v.toLocaleString("pt-BR")}%`;
  return `R$ ${fmtMoeda(v)}`;
}

function linhaItemMd(d: DivergenciaItem): string {
  const partes = [
    `- **[${d.severidade}/${d.status}]** ${d.descricao}`,
    `  - id: \`${d.id}\` · nível ${d.nivel} · alvo \`${d.alvo}\``,
  ];
  if (d.calculado !== null && d.calculado !== undefined) {
    const delta = d.delta !== null && d.delta !== undefined ? ` · Δ ${d.delta >= 0 ? "+" : ""}${fmtValorExport(d.delta, d.unidade)}` : "";
    partes.push(`  - interno ${fmtValorExport(d.calculado, d.unidade)} × retorno ${fmtValorExport(d.esperado, d.unidade)}${delta}`);
  }
  if (d.justificativa) {
    partes.push(`  - ciência: ${d.justificativa}${d.referenciaEstudo ? ` (ref.: ${d.referenciaEstudo})` : ""}`);
  }
  return partes.join("\n");
}

function subGrupoMd(sg: GrupoDivergencias["subGrupos"][number]): string {
  return [`#### ${sg.rotulo} (${sg.itens.length} ocorrência(s)${sg.abertas > 0 ? `, ${sg.abertas} aberta(s)` : ""})`, "", ...sg.itens.map(linhaItemMd)].join("\n");
}

/** Documento Markdown completo (mesma narrativa dos grupos da tela). */
export function montarExportMarkdown(divergencias: DivergenciaItem[], ctx: ContextoExportDivergencias): string {
  const grupos = agruparDivergencias(divergencias);
  const r = resumoDaLista(divergencias);
  const contagem = (m: Record<string, number>) =>
    Object.entries(m)
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
      .map(([k, n]) => `${k}: ${n}`)
      .join(" · ");
  const cabecalho = [
    `# Divergências da conferência — competência ${ctx.competenciaRotulo}`,
    "",
    `- exportado em: ${ctx.geradoEmIso}`,
    ctx.origemUrl ? `- origem: ${ctx.origemUrl}` : null,
    ctx.retornoId ? `- retorno Contmega: \`${ctx.retornoId}\`` : null,
    ctx.runId ? `- run de referência: \`${ctx.runId}\`` : null,
    `- schema do JSON irmão: \`${SCHEMA_VERSAO_DIVERGENCIAS_EXPORT}\``,
    "",
    `## Resumo`,
    "",
    `- total: ${r.total} divergência(s) em ${grupos.length} grupo(s)`,
    `- por severidade: ${contagem(r.porSeveridade)}`,
    `- por status: ${contagem(r.porStatus)}`,
    `- bloqueantes abertas: ${r.bloqueantesAbertas}`,
  ].filter((l): l is string => l !== null);
  const corpo = grupos.flatMap((g) => [
    "",
    `## ${g.classe} (${g.itens.length} — ${g.abertas} aberta(s), ${g.bloqueantesAbertas} bloqueante(s) aberta(s))`,
    "",
    ...g.subGrupos.flatMap((sg) => [subGrupoMd(sg), ""]),
  ]);
  return [...cabecalho, ...corpo].join("\n").replace(/\n{3,}/g, "\n\n").trimEnd() + "\n";
}

/** Nome do arquivo do export: divergencias-2026-08-20260901-184502.md/.json. */
export function nomeArquivoExport(competenciaId: number, formato: "md" | "json", geradoEm: Date): string {
  const aaaa = Math.floor(competenciaId / 100);
  const mm = String(competenciaId % 100).padStart(2, "0");
  const stamp = `${geradoEm.getFullYear()}${String(geradoEm.getMonth() + 1).padStart(2, "0")}${String(geradoEm.getDate()).padStart(2, "0")}-${String(geradoEm.getHours()).padStart(2, "0")}${String(geradoEm.getMinutes()).padStart(2, "0")}${String(geradoEm.getSeconds()).padStart(2, "0")}`;
  return `divergencias-${aaaa}-${mm}-${stamp}.${formato}`;
}
